import React from "react";
import GetHeroIcon from "../../utils/getHeroIcon";
import withTelemetry from "../molecules/analytics/components/withTelemetry";
import { cn } from "../../utils/cn";
import { clockNumberClasses } from "@mui/x-date-pickers";

type AsProp<C extends React.ElementType> = {
  as?: C;
};
type Props<C extends React.ElementType = "button"> = AsProp<C> &
  Omit<React.ComponentPropsWithoutRef<C>, "as" | "disable"> & {
    label?: string | JSX.Element | JSX.Element[] | (() => JSX.Element);
    variant?: "outlined" | "default" | "contained";
    iconPosition?: "leading" | "trailing";
    iconName?: string;
    iconOnly?: boolean;
    buttonColor?: "primary" | "secondary" | "warning" | "amber";
    rounded?: "md" | "lg" | "full";
    size?: "sm" | "md" | "lg" | "xl";
    disable?: boolean;
    testId?: string;
    iconVariant?: "solid" | "outline";
    isLoading?: boolean;
    loadingLabel?: string;
    loaderClass?: string;
    loadingLabelClass?: string;
    onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
    className?: string;
    href?: string;
    children?: React.ReactNode;
  };

const Button = <C extends React.ElementType = "button">(props: Props<C>) => {
  const {
    as,
    href,
    onClick,
    label,
    variant,
    iconPosition,
    iconName,
    iconOnly,
    rounded,
    size,
    disable = false,
    buttonColor,
    testId,
    iconVariant = "solid",
    className,
    children,
    isLoading,
    loadingLabel,
    loaderClass,
    loadingLabelClass,
    ...rest
  } = props;

  const Component = as || "button";

  const buttonClasses = cn(
    `${disable ? "cursor-not-allowed" : "cursor-pointer"}`,
    "inline-flex items-center gap-x-2 justify-center transition-colors duration-150 select-none",
    "capitalize text-sm font-semibold ring-1 ring-inset px-3.5 py-2.5 ring-secondary/20",
    "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary/90",
    "active:scale-[0.98]",
    "disabled:bg-bkg disabled:border-border dark:disabled:bg-gray-50 disabled:text-border disabled:ring-border disabled:hover:border-border disabled:hover:bg-bkg dark:disabled:bg-[#525252] dark:disabled:text-[#f5f5f5] aria-disabled:bg-bkg aria-disabled:border-border aria-disabled:text-border aria-disabled:ring-border aria-disabled:hover:border-border aria-disabled:hover:bg-bkg dark:aria-disabled:bg-[#525252] dark:aria-disabled:text-[#f5f5f5]",
    size === "sm" && "px-2 py-1",
    size === "md" && "px-2.5 py-1.5",
    size === "lg" && "px-3 py-2",
    size === "xl" && "px-3.5 py-2.5",
    rounded && `rounded-${rounded}`,
    !rounded && "rounded-md",
    iconPosition && "inline-flex items-center gap-x-2",
    variant === "contained" &&
      buttonColor === "primary" &&
      "bg-primary text-white shadow-sm hover:bg-primary/90 hover:text-white focus-visible:outline-primary/90",
    variant === "contained" &&
      buttonColor === "secondary" &&
      "bg-secondary dark:bg-[#3b3b3b] dark:hover:bg-[#3b3b3b]/90 text-white shadow-sm hover:bg-secondary/90 hover:text-white focus-visible:outline-secondary/90",
    variant === "contained" &&
      buttonColor === "amber" &&
      "bg-amber-400 text-white shadow-sm hover:bg-amber-400/90 hover:text-white focus-visible:outline-amber-400/90",
    variant === "contained" &&
      buttonColor === "warning" &&
      "bg-red-600 text-white shadow-sm hover:bg-red-600/90 hover:text-white focus-visible:outline-red-600/90",
    variant === "contained" &&
      !buttonColor &&
      "bg-primary text-white shadow-sm hover:bg-primary/90 hover:text-white focus-visible:outline-primary/90",
    variant === "outlined" &&
      buttonColor === "primary" &&
      "bg-bkg text-primary/80 shadow-sm ring-1 ring-inset ring-primary/20 hover:bg-primary/5",
    variant === "outlined" &&
      buttonColor === "secondary" &&
      "bg-bkg text-secondary/80 shadow-sm ring-1 ring-inset ring-secondary/20 hover:bg-secondary/5",
    variant === "outlined" &&
      buttonColor === "warning" &&
      "bg-bkg text-red-700 shadow-sm ring-1 ring-inset ring-red-200 hover:bg-red-50 focus-visible:outline-red-600/90 dark:bg-zinc-900 dark:text-red-300 dark:ring-red-900/60 dark:hover:bg-red-950/20",
    variant === "outlined" &&
      !buttonColor &&
      "bg-bkg text-primary/80 shadow-sm ring-1 ring-inset ring-primary/20 hover:bg-primary/5",
    variant === "default" &&
      buttonColor === "secondary" &&
      "text-secondary/80 hover:rounded-md hover:shadow-sm hover:ring-1 hover:ring-inset ring-secondary/20 hover:bg-secondary/5",
    variant === "default" &&
      !buttonColor &&
      "text-primary/80 hover:rounded-md hover:shadow-sm hover:ring-1 hover:ring-inset ring-primary/20 hover:bg-primary-light",
    disable && "disabled",
    className
  );

  const componentProps: any = {
    className: buttonClasses,
    "data-testid": testId,
    onClick,
    ...rest
  };

  if (Component === "button") {
    componentProps.type = "button";
    componentProps.disabled = disable;
  } else if (disable) {
    componentProps["aria-disabled"] = true;
    componentProps.onClick = (e: React.MouseEvent) => {
      e.preventDefault();
    };
    componentProps.tabIndex = -1; // prevent tab focus
    componentProps.className = cn(
      componentProps.className,
      "pointer-events-none opacity-50 cursor-not-allowed"
    );
  }
  if (isLoading) {
    return (
      <Component {...componentProps}>
        <div
          className={cn(
            "w-4 h-4 border-2 border-gray-300 border-t-green-500 rounded-full animate-spin mr-2",
            loaderClass
          )}
        />
        {/* <svg
          aria-hidden="true"
          className={cn(
            "h-4 aspect-square inline mr-2 text-gray-200 animate-spin dark:text-white fill-green-500",
            loaderClass
          )}
          viewBox="0 0 100 101"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        ></svg> */}
        <p className={cn(loadingLabelClass)}>{loadingLabel ?? "Loading..."}</p>
      </Component>
    );
  }
  return (
    <Component {...componentProps}>
      {iconOnly && iconName && (
        <GetHeroIcon
          name={iconName}
          className="-mr-0.5 w-5 h-5 py-1"
          aria-hidden="true"
          variant={iconVariant}
        />
      )}
      {iconName && iconPosition !== "trailing" && (
        <>
          <GetHeroIcon
            name={iconName}
            className="-ml-0.5 w-5 h-5 py-0.5"
            aria-hidden="true"
            variant={iconVariant}
          />
          {label}
        </>
      )}
      {iconName && iconPosition === "trailing" && (
        <>
          {label}
          <GetHeroIcon
            name={iconName}
            className="-mr-0.5 w-5 h-5 py-0.5"
            aria-hidden="true"
            variant={iconVariant}
          />
        </>
      )}
      {!iconName && <>{label}</>}
      {children}
    </Component>
  );
};

const TelemetryButton = withTelemetry(Button);
export default TelemetryButton;
